import React, { useState, useEffect } from 'react';
import api from '../../api';
import CustomAutocomplete from '../utils/CustomAutocomplete';
import PartnerDetails from './PartnerDetails';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import './PartnerManager.css';

const PartnerManager = ({ showToast, currentUser }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditPartnerModal, setShowEditPartnerModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [existingCompanies, setExistingCompanies] = useState([]);
  const [detailsData, setDetailsData] = useState(null);
  
  // Form states
  const [partnerName, setPartnerName] = useState('');
  const [userName, setUserName] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [editingUser, setEditingUser] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);

  useEffect(() => {
    loadPartners();
    loadCompanies();
  }, []);

  useEffect(() => {
    if (detailsData) {
      loadPartnerUsers(detailsData.id);
    }
  }, [detailsData]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/partner/admin/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to load partners:', error);
      showToast('Failed to load partners', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setExistingCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadPartnerUsers = async (partnerId) => {
    try {
      const response = await api.get(`/partner/admin/partners/${partnerId}/users`);
      setPartnerUsers(response.data);
    } catch (error) {
      console.error('Failed to load partner users:', error);
      showToast('Failed to load users', 'error');
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    if (!partnerName.trim()) {
      showToast('Partner name is required', 'error');
      return;
    }

    try {
      await api.post('/partner/admin/partners', { name: partnerName });
      showToast('Partner created successfully', 'success');
      setShowCreatePartnerModal(false);
      setPartnerName('');
      loadPartners();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to create partner';
      showToast(errorMsg, 'error');
    }
  };

  const handleUpdatePartner = async (e) => {
    e.preventDefault();
    if (!editingPartner) return;

    try {
      await api.put(`/partner/admin/partners/${editingPartner.id}`, {
        name: partnerName,
        isActive: editingPartner.isActive
      });
      showToast('Partner updated successfully', 'success');
      setShowEditPartnerModal(false);
      setEditingPartner(null);
      setPartnerName('');
      loadPartners();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update partner';
      showToast(errorMsg, 'error');
    }
  };

  const handleDeletePartner = async (partnerId, partnerName) => {
    if (!window.confirm(`Are you sure you want to delete "${partnerName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/partner/admin/partners/${partnerId}`);
      showToast('Partner deleted successfully', 'success');
      if (detailsData?.id === partnerId) {
        setDetailsData(null);
        setPartnerUsers([]);
      }
      loadPartners();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete partner';
      showToast(errorMsg, 'error');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userName.trim() || !userPassword.trim()) {
      showToast('Username and password are required', 'error');
      return;
    }

    try {
      await api.post(`/partner/admin/partners/${detailsData.id}/users`, {
        username: userName,
        password: userPassword,
        email: userEmail,
        fullName: userFullName,
        role: userRole
      });
      showToast('User created successfully', 'success');
      setShowCreateUserModal(false);
      resetUserForm();
      loadPartnerUsers(detailsData.id);
      
      // Reload partners and refresh the detailsData to update PartnerDetails
      await loadPartners();
      // Fetch the updated partner to get the new user count
      try {
        const response = await api.get('/partner/admin/partners');
        const updatedPartner = response.data.find(p => p.id === detailsData.id);
        if (updatedPartner) {
          setDetailsData({ ...updatedPartner });
        }
      } catch (err) {
        console.error('Error refreshing partner data:', err);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to create user';
      showToast(errorMsg, 'error');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    const payload = {
      username: userName,
      email: userEmail,
      fullName: userFullName,
      isActive: editingUser.isActive,
      role: userRole
    };

    if (userPassword.trim()) {
      payload.password = userPassword;
    }

    try {
      await api.put(`/partner/admin/users/${editingUser.id}`, payload);
      showToast('User updated successfully', 'success');
      setShowEditUserModal(false);
      setEditingUser(null);
      resetUserForm();
      loadPartnerUsers(detailsData.id);
      
      // Reload partners and refresh the detailsData to update PartnerDetails
      await loadPartners();
      try {
        const response = await api.get('/partner/admin/partners');
        const updatedPartner = response.data.find(p => p.id === detailsData.id);
        if (updatedPartner) {
          setDetailsData({ ...updatedPartner });
        }
      } catch (err) {
        console.error('Error refreshing partner data:', err);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update user';
      showToast(errorMsg, 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/partner/admin/users/${userId}`);
      showToast('User deleted successfully', 'success');
      loadPartnerUsers(detailsData.id);
      
      // Reload partners and refresh the detailsData to update PartnerDetails
      await loadPartners();
      try {
        const response = await api.get('/partner/admin/partners');
        const updatedPartner = response.data.find(p => p.id === detailsData.id);
        if (updatedPartner) {
          setDetailsData({ ...updatedPartner });
        }
      } catch (err) {
        console.error('Error refreshing partner data:', err);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete user';
      showToast(errorMsg, 'error');
    }
  };

  const resetUserForm = () => {
    setUserName('');
    setUserPassword('');
    setUserEmail('');
    setUserFullName('');
    setUserRole('user');
  };

  const openEditPartnerModal = (partner) => {
    setEditingPartner({ ...partner });
    setPartnerName(partner.name);
    setShowEditPartnerModal(true);
  };

  const openEditUserModal = (user) => {
    setEditingUser({ ...user });
    setUserName(user.username);
    setUserEmail(user.email || '');
    setUserFullName(user.fullName || '');
    setUserRole(user.role || 'user');
    setUserPassword('');
    setShowEditUserModal(true);
  };

  if (loading) {
    return <div className="partner-manager-loading">Loading partners...</div>;
  }

  return (
    <div className="partner-manager-container">
      <div className="partner-manager-header">
        <button className="btn-primary" onClick={() => setShowCreatePartnerModal(true)}>
          + Create Partner
        </button>
      </div>

      <div className="partner-manager-content">
        {/* Partners List */}
        <div className="partner-list-panel" style={{ maxWidth: '100%' }}>
          <h2>Partners ({partners.length})</h2>
          {partners.length === 0 ? (
            <div className="empty-state">
              <p>No partners yet.</p>
              <p>Create one to get started!</p>
            </div>
          ) : (
            <div className="partner-list">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className={`partner-card ${detailsData?.id === partner.id ? 'selected' : ''}`}
                  onClick={() => setDetailsData(partner)}
                >
                  <div className="partner-card-header">
                    <h3>{partner.name}</h3>
                    <span className={`status-badge ${partner.isActive ? 'active' : 'inactive'}`}>
                      {partner.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="partner-card-stats">
                    <div className="stat">
                      <span className="stat-value">{partner.userCount}</span>
                      <span className="stat-label">Users</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{partner.formCount}</span>
                      <span className="stat-label">Forms</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{partner.boxCount}</span>
                      <span className="stat-label">Boxes</span>
                    </div>
                  </div>
                  <div className="partner-card-actions">
                    <button
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditPartnerModal(partner);
                      }}
                      title="Edit"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePartner(partner.id, partner.name);
                      }}
                      title="Delete"
                      style={{ color: 'var(--danger-color, #dc3545)' }}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Partner Modal */}
      {showCreatePartnerModal && (
        <div className="modal-overlay" onClick={() => setShowCreatePartnerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create Partner</h2>
            <form onSubmit={handleCreatePartner}>
              <div className="form-group">
                <label>Partner Name *</label>
                <CustomAutocomplete
                  value={partnerName}
                  onChange={(val) => setPartnerName(val)}
                  onBlur={() => {}}
                  suggestions={existingCompanies.map(company => company.name)}
                  placeholder="e.g., Acme Corporation"
                  chipStyle={false}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowCreatePartnerModal(false);
                  setPartnerName('');
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Partner Modal */}
      {showEditPartnerModal && editingPartner && (
        <div className="modal-overlay" onClick={() => setShowEditPartnerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Partner</h2>
            <form onSubmit={handleUpdatePartner}>
              <div className="form-group">
                <label>Partner Name *</label>
                <CustomAutocomplete
                  value={partnerName}
                  onChange={(val) => setPartnerName(val)}
                  onBlur={() => {}}
                  suggestions={existingCompanies.map(company => company.name)}
                  chipStyle={false}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editingPartner.isActive}
                    onChange={(e) => setEditingPartner({ ...editingPartner, isActive: e.target.checked })}
                  />
                  {' '}Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowEditPartnerModal(false);
                  setEditingPartner(null);
                  setPartnerName('');
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add User to {detailsData?.name}</h2>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={userFullName}
                  onChange={(e) => setUserFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowCreateUserModal(false);
                  resetUserForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit User</h2>
            <form onSubmit={handleUpdateUser}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  placeholder="New password"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={userFullName}
                  onChange={(e) => setUserFullName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editingUser.isActive}
                    onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.checked })}
                  />
                  {' '}Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                  resetUserForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Partner Details Overlay */}
      {detailsData && (
        <PartnerDetails
          data={detailsData}
          onClose={() => setDetailsData(null)}
          onSave={async (updatedData) => {
            await loadPartners();
            setDetailsData(updatedData);
            return true;
          }}
          showToast={showToast}
          onAddUser={() => setShowCreateUserModal(true)}
          onEditUser={(user) => openEditUserModal(user)}
          onDeleteUser={async (user) => {
            if (window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
              await handleDeleteUser(user.id);
            }
          }}
        />
      )}
    </div>
  );
};

export default PartnerManager;

